import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import {
    CreateCourseRelevantExecutionSuccess,
    DeselectSelectedCourseRelevantExecutions,
    SearchCourseRelevantExecutions,
    SelectFilteredCourseRelevantExecutions,
    SetCourseRelevantExecutions,
    ToggleSelectedCourseRelevantExecution,
} from '../actions';
import { AddCourseRelevantExecutions } from '../actions/add-entities.action';
import { CourseRelevantExecution } from './course-relevant-execution.model';
import { CourseRelevantExecutionState } from './course-relevant-execution.state';
import { CourseRelevantExecutionsStateModel } from './course-relevant-executions.state-model';

@State<CourseRelevantExecutionsStateModel>({
    name: 'courseRelevantExecutions',
    defaults: new CourseRelevantExecutionsStateModel(),
})
@Injectable()
export class CourseRelevantExecutionsState extends SubentityState<CourseRelevantExecution> {
    @Selector([CourseRelevantExecutionState.getEntities])
    static getFilteredEntities(state: CourseRelevantExecutionsStateModel, courseRelevantExecutions: CourseRelevantExecution[]): CourseRelevantExecution[] {
        if (!courseRelevantExecutions) {
            return null;
        }

        return courseRelevantExecutions
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, CourseRelevantExecution], b: [number, CourseRelevantExecution]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, CourseRelevantExecution]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: CourseRelevantExecutionsStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static isFetchingList(state: CourseRelevantExecutionsStateModel): boolean {
        return state.fetchingList;
    }

    @Selector()
    static getSearchTerm(state: CourseRelevantExecutionsStateModel): string {
        return state.searchTerm;
    }

    constructor(private store: Store) {
        super(null, 0);

        this._propstoSearch = [...CourseRelevantExecution.props_to_search];
    }

    @Action(AddCourseRelevantExecutions)
    addEntities(ctx: StateContext<CourseRelevantExecutionsStateModel>, action: AddCourseRelevantExecutions): void {
        const relevantExecutions = this.store.selectSnapshot(CourseRelevantExecutionState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map((entity) => entity.id) } }, relevantExecutions);
    }

    @Action(CreateCourseRelevantExecutionSuccess)
    createEntity(ctx: StateContext<CourseRelevantExecutionsStateModel>, action: CreateCourseRelevantExecutionSuccess): void {
        const relevantExecutions = this.store.selectSnapshot(CourseRelevantExecutionState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, relevantExecutions);
    }

    @Action(SetCourseRelevantExecutions)
    setEntities(ctx: StateContext<CourseRelevantExecutionsStateModel>, action: SetCourseRelevantExecutions): void {
        this._setSubentities(ctx, action, null);
    }

    @Action(ToggleSelectedCourseRelevantExecution)
    toggleSelect(ctx: StateContext<CourseRelevantExecutionsStateModel>, action: ToggleSelectedCourseRelevantExecution): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredCourseRelevantExecutions)
    selectAll(ctx: StateContext<CourseRelevantExecutionsStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedCourseRelevantExecutions)
    deselectAll(ctx: StateContext<CourseRelevantExecutionsStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchCourseRelevantExecutions)
    search({ patchState }: StateContext<CourseRelevantExecutionsStateModel>, action: SearchCourseRelevantExecutions): void {
        patchState({
            searchTerm: action.payload,
        });
    }
}
